<template>
  <div class="position-relative">
    <t-loader :loading="isLoading"/>
    <form @submit="submit" :disabled="!saveable">
      <div class="card">
        <div class="card-header">
          {{ title }}
          <br>
          <small>{{ description }}</small>
        </div>
        <div class="card-body text-readable">
          <div v-if="multipleChoice">
            <label>Choose how you want to receive payments</label>
            <div class="form-group pt-1" style="max-width: 23rem">
              <template v-for="(method, key) in methods">
                <div v-if="method.available" class="form-check" :class="{ selected: key === selected }">
                  <input class="form-check-input" type="radio" name="method" :value="key" v-model="selection" :id="key">
                  <label class="form-check-label" :for="key">
                    <span>{{ method.title }}</span>
                    <b-badge v-if="key === active" variant="success" pill>Active</b-badge>
                  </label>
                </div>
              </template>
            </div>
          </div>
          <div class="alert alert-warning" v-if="active !== selected && !multipleChoice">
            These settings are inactive please click apply changes to activate this payment method.
          </div>
          <table class="table table-striped table-list">
            <tbody>
            <template v-if="selected === 'bankgiro'">
              <tr>
                <th>
                  <label for="bankName">Bank Name</label>
                </th>
                <td>
                  <div class="form-group">
                    <input type="text" id="bankName" name="bankName"
                           v-model="methods.bankgiro.data.bankName"
                           class="form-control form-control-sm"
                           :class="{ 'is-invalid': methods.paypal.errors.hasOwnProperty('bankName')}"
                           autocomplete="off"/>
                    <input-feedback :errors="methods.bankgiro.errors" name="bankName"/>
                  </div>
                </td>
              </tr>
              <tr>
                <th>
                  <label for="accountNumber">Account Number</label>
                </th>
                <td>
                  <div class="form-group">
                    <input type="text" id="accountNumber" name="accountNumber"
                           v-model="methods.bankgiro.data.accountNumber"
                           class="form-control form-control-sm"
                           :class="{ 'is-invalid': methods.bankgiro.errors.hasOwnProperty('accountNumber')}"
                           autocomplete="off"/>
                    <input-feedback :errors="methods.bankgiro.errors" name="accountNumber"/>
                  </div>
                </td>
              </tr>
              <tr>
                <th>
                  <label for="clearingNumber">Clearing Number</label>
                </th>
                <td>
                  <div class="form-group">
                    <input type="text" id="clearingNumber" name="clearingNumber"
                           v-model="methods.bankgiro.data.clearingNumber"
                           class="form-control form-control-sm"
                           :class="{ 'is-invalid': methods.bankgiro.errors.hasOwnProperty('clearingNumber')}"
                           autocomplete="off"/>
                    <input-feedback :errors="methods.bankgiro.errors" name="clearingNumber"/>
                  </div>
                </td>
              </tr>
            </template>
            <template v-else-if="selected === 'iban'">
              <tr>
                <th>
                  <label for="accountNumber">IBAN</label>
                </th>
                <td>
                  <div class="form-group">
                    <input type="text" id="iban" name="iban"
                           v-model="methods.iban.data.number"
                           class="form-control form-control-sm"
                           :class="{ 'is-invalid': methods.iban.errors.hasOwnProperty('number')}"
                           placeholder=""
                           autocomplete="off"/>
                    <input-feedback :errors="methods.iban.errors" name="number"/>
                  </div>
                </td>
              </tr>
              <tr>
                <th>
                  <label for="clearingNumber">BIC (SWIFT)</label>
                  <div class="text-muted">(for SEPA countries, an “IBAN-only principle” applies)</div>
                </th>
                <td>
                  <div class="form-group">
                    <input type="text" id="bic" name="bic"
                           v-model="methods.iban.data.bic"
                           class="form-control form-control-sm"
                           :class="{ 'is-invalid': methods.iban.errors.hasOwnProperty('bic')}"
                           placeholder=""
                           autocomplete="off"/>
                    <input-feedback :errors="methods.iban.errors" name="bic"/>
                  </div>
                </td>
              </tr>
            </template>
            <template v-else-if="selected === 'paypal'">
              <tr>
                <th>
                  <label for="paypalEmail">E-mail</label>
                </th>
                <td>
                  <div class="form-group">
                    <input type="text" id="paypalEmail" name="paypalEmail"
                           v-model="methods.paypal.data.paypalEmail"
                           class="form-control form-control-sm"
                           :class="{ 'is-invalid': methods.paypal.errors.hasOwnProperty('paypalEmail')}"
                           autocomplete="off"/>
                    <input-feedback :errors="methods.paypal.errors" name="paypalEmail"/>
                    <div class="mt-2">
                      <em>Please ensure that the given e-mail is linked to an existing PayPal account.</em>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
          <div v-if="selected === 'paypal'" class="text-readable my-3 text-warning text-center">
            <ion-icon name="warning" class="mr-1"></ion-icon>
            <em class="small align-text-top">Charge does not compensate for any PayPal transaction fees.</em>
          </div>
        </div>
        <div class="card-footer">
          <button type="submit" :disabled="!saveable" class="btn btn-primary">
            Update
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import TLoader from '#/js/components/TLoader';
import InputFeedback from '#/js/components/InputFeedback';

export default {
  name: 'UserPaymentMethod',
  components: {InputFeedback, TLoader},
  data() {
    return {
      isLoading: true,
      active: null,
      selection: null,
      methods: {
        bankgiro: {
          available: false,
          title: 'Bank Account',
          description: 'Swedish bank account details',
          route: 'account/bank',
          data: {
            bankName: null,
            accountNumber: null,
            clearingNumber: null,
          },
          errors: {},
        },
        iban: {
          available: false,
          title: 'IBAN',
          description: 'International Bank Account details',
          route: 'account/iban',
          data: {
            number: null,
            bic: null,
          },
          errors: {},
        },
        paypal: {
          available: false,
          title: 'PayPal',
          description: 'PayPal account details',
          route: 'account/paypal',
          data: {
            paypalEmail: null,
          },
          errors: {},
        },
      },
    };
  },
  computed: {
    method() {
      if (this.selected !== null && this.methods.hasOwnProperty(this.selected)) {
        return this.methods[this.selected];
      }
      return {};
    },
    saveable() {
      if (this.method) {
        return this.method.available;
      }

      return false;
    },
    selected() {
      if (this.selection) {
        return this.selection;
      }

      // If there is only one choice we can select it by default.
      if (!this.multipleChoice) {
        for (let [key, data] of Object.entries(this.methods)) {
          if (data.available) {
            return key;
          }
        }
      }

      return null;
    },
    multipleChoice() {
      return Object.values(this.methods).filter(data => data.available).length > 1;
    },
    title() {
      return this.method.title || 'Payment';
    },
    description() {
      return this.method.description || 'Select a payment method';
    },
  },
  methods: {
    submit(e) {
      e.preventDefault();

      const method = this.method;
      if (!method) {
        return;
      }

      this.isLoading = true;
      console.log('submit?');

      axios.patch(method.route, method.data).then((response) => {
        Swal.fire({
          icon: 'success',
          text: response.data.message || `Successfully updated ${method.title}`,
        });
        this.flushErrors();
        this.refresh();
      }).catch((response) => {
        const data = response.response.data || {};
        method.errors = data.errors || null;

        if (!data.hasOwnProperty('errors')) {
          Swal.fire({
            icon: 'error',
            text: data.message || 'Something went wrong, please try again later.',
          });
        }

        this.isLoading = false;
      });
    },
    flushErrors() {
      Object.values(this.methods).forEach((method) => {
        method.errors = {};
      });
    },
    refresh() {
      this.isLoading = true;
      axios.get('/account/paymentMethod/status').then((response) => {
        const data = response.data;
        if (data.hasOwnProperty('available')) {
          const available = data.available;

          this.methods.bankgiro.route = data.routes.bankgiro;
          this.methods.paypal.route = data.routes.paypal;
          this.methods.iban.route = data.routes.iban;

          if (available.bankgiro) {
            this.methods.bankgiro.available = true;
            this.methods.bankgiro.data.accountNumber = available.bankgiro.accountNumber;
            this.methods.bankgiro.data.clearingNumber = available.bankgiro.clearingNumber;
            this.methods.bankgiro.data.bankName = available.bankgiro.bankName;
          }
          if (available.paypal) {
            this.methods.paypal.available = true;
            this.methods.paypal.data.paypalEmail = available.paypal.email;
          }
          if (available.iban) {
            this.methods.iban.available = true;
            this.methods.iban.data.number = available.iban.number;
            this.methods.iban.data.bic = available.iban.bic;
          }
        }

        this.active = data.selected || null;
        if (this.active) {
          const method = this.methods[this.active] || null;
          if (method && method.available && this.selection === null) {
            this.selection = this.active || null;
          }
        }

        this.isLoading = false;
      });
    },
  },
  mounted() {
    this.refresh();
  },
};
</script>

<style lang="scss" scoped>
.form-check {
  margin-bottom: .2rem;
  font-weight: 600;
  font-size: .9rem;
  color: #bfc2ce;
  border: 2px solid #ffffff00;

  &.selected {
    color: white;
  }
}
</style>